import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Capability, SessionInfo } from "@hik/contracts";
import { ApiError, get, post, setApiLocale } from "./api";

interface SessionCtx {
  session: SessionInfo | null;
  loading: boolean;
  refresh: () => Promise<unknown>;
  logout: () => Promise<void>;
  can: (cap: Capability) => boolean;
  /** Yöneticilik raporlama ilişkisinden gelir (atanmış rol değildir). */
  isManager: boolean;
  /** Oturum süresi dolduğu için mi kapandı (giriş ekranı nedenini söyler: `?neden=sure`). */
  expired: boolean;
  /** Kişi "Çıkış yap"a bastı: giriş ekranı kaldığı sayfaya dönüş (`?sonra=`) önermez. */
  loggedOut: boolean;
}

const Ctx = createContext<SessionCtx | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [expired, setExpired] = useState(false);
  const [loggedOut, setLoggedOut] = useState(false);
  const q = useQuery({
    queryKey: ["session"],
    queryFn: async () => {
      try {
        return await get<SessionInfo>("/auth/session");
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          if (e.code === "session_expired") setExpired(true);
          return null;
        }
        throw e;
      }
    },
    staleTime: 60_000,
    retry: false,
  });

  useEffect(() => {
    if (q.data?.user.locale) setApiLocale(q.data.user.locale);
  }, [q.data?.user.locale]);

  // Herhangi bir istek 401 dönerse oturum yenilenir (süresi dolmuşsa giriş ekranına düşülür).
  useEffect(() => {
    const onUnauth = (ev: Event) => {
      if ((ev as CustomEvent<string>).detail === "session_expired") setExpired(true);
      qc.setQueryData(["session"], null);
    };
    window.addEventListener("hik:unauthenticated", onUnauth);
    return () => window.removeEventListener("hik:unauthenticated", onUnauth);
  }, [qc]);

  const session = q.data ?? null;
  // Yeniden giriş yapılınca "süresi doldu" bilgisi sıfırlanır.
  useEffect(() => {
    if (session) {
      setExpired(false);
      setLoggedOut(false);
    }
  }, [session]);
  const value: SessionCtx = {
    session,
    loading: q.isPending,
    refresh: () => q.refetch(),
    logout: async () => {
      setExpired(false);
      setLoggedOut(true);
      try {
        await post("/auth/logout");
      } finally {
        // Önce oturum boşaltılır (gözlemcisi haber alır, giriş ekranına yönlenilir), sonra diğer veriler silinir.
        // queryClient.clear() gözlemcilere haber vermediğinden ekran oturum açık kalıyordu.
        qc.setQueryData(["session"], null);
        qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "session" });
      }
    },
    can: (cap) => Boolean(session?.capabilities.includes(cap)),
    isManager: Boolean(session?.employee?.hasReports),
    expired,
    loggedOut,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("SessionProvider missing");
  return v;
}

/** Oturum zorunlu bileşenler için: session null değil. */
export function useAuthed() {
  const s = useSession();
  if (!s.session) throw new Error("not authenticated");
  return { ...s, session: s.session };
}
